import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  isOnlinePanelOpenAtom,
  IVehicleOnlinePanels,
  IWidgetGraph,
  onlinePanelAtom,
} from '../OnlinePanelState';
import { ICustomWidgetProps } from './Widget';
import { IVehicleData } from '../../Vehicles/types';
import { ordinaryWidgetGraphAtom } from '../../Graphs/GraphState';
import RemoveButton from './RemoveButton';
import { getWidgetTooltip, getWidgetValue, isWidgetDisabled } from './WidgetService';
import { vehicleDataByIdSelector } from '../../../store/recoil/vehiclesData';
import useAnalytics from '../../../hooks/useAnalytics';

export const RPM = 'rpm';

export default function RpmWidget(props: ICustomWidgetProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const setOrdinaryWidgetGraph = useSetRecoilState<IWidgetGraph | null>(ordinaryWidgetGraphAtom);
  const [widgetData, setWidgetData] = React.useState<number | null>(2500);
  const valueTypeLong = f({ id: 'widget.rpm.valueType.long' });
  const valueTypeShort = f({ id: 'widget.rpm.valueType.short' });
  const vehicleId = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const vehicleData = useRecoilValue<IVehicleData | null>(vehicleDataByIdSelector(vehicleId));
  const onlinePanelData = useRecoilValue<IVehicleOnlinePanels>(onlinePanelAtom);
  const shortName = f({ id: 'widget.rpm.valueName.short' });
  const longName = f({ id: 'widget.rpm.valueName.long' });

  React.useMemo(() => {
    if (!props.isMockupMode) {
      const onlinePanel = onlinePanelData[Number(vehicleId)];

      if (onlinePanel && onlinePanel.rpm !== null) {
        const roundedValue = Number(onlinePanel.rpm).toFixed(2);
        setWidgetData(Number(roundedValue));
      } else {
        setWidgetData(null);
      }
    }
  }, [onlinePanelData, props.isMockupMode, vehicleId]);

  const handleDetail = () => {
    if (typeof widgetData === 'number' && vehicleData) {
      // const rideId = vehicleData.lastRide?.id;
      trackEvent(['Rpm Widget', 'click', 'Open Rpm graph']);
      setOrdinaryWidgetGraph({
        vehicleId: vehicleId || 0,
        name: RPM,
      });
    }
  };

  return (
    <div
      onClick={props.isMockupMode ? undefined : handleDetail}
      style={props.isMockupMode ? undefined : { cursor: 'pointer' }}
      data-cy="rpm-widget"
      className={`b-widget is-clickable b-widget--${props.data.rowSize}x${props.data.columnSize} ${
        props.data.hasIcon && props.data.columnSize > 1 ? 'b-widget--alignLeft' : ''
      } widget-grid-name-${props.data.gridName} ${props.isInGrid ? '' : 'droppable-element'}`}
      draggable={!props.isInGrid}
      onDragStart={props.isInGrid ? undefined : props.onDragCallback}
      title={getWidgetTooltip(
        f({ id: 'widget.rpm.tooltip' }),
        f({ id: 'onlineTemplates.subPage.section.widgets.tooltip' }),
        props.isInGrid,
        props.isMockupMode
      )}
    >
      {props.isInGrid && (
        <RemoveButton
          dataCy="rpm-widget-in-grid"
          onRemoveCallback={props.onRemoveCallback}
          gridName={props.data.gridName}
        />
      )}
      {props.data.hasTitle && props.data.columnSize === 1 && !props.data.hasIcon && (
        <h4 data-cy="rpm-widget-title-size-1" className="e-widget-title">
          {shortName}
        </h4>
      )}
      {props.data.hasTitle && props.data.columnSize > 1 && !props.data.hasIcon && (
        <h4 data-cy="rpm-widget-title-size-more-1" className="e-widget-title">
          {longName}
        </h4>
      )}
      {props.data.hasTitle && props.data.columnSize === 2 && props.data.hasIcon && (
        <h4 data-cy="rpm-widget-title-size-more-2" className="e-widget-title">
          {longName}
        </h4>
      )}
      {props.data.hasIcon && props.data.columnSize > 1 && (
        <div className="row no-gutters" data-cy="rpm-widget-title-icon-size-more-1">
          <div className="col-auto">
            {props.data.columnSize === 2 && (
              <i
                className={`e-widget-icon cmd-icon cmd-icon-rotation-speed ${isWidgetDisabled(
                  widgetData
                )}`}
              />
            )}
            {props.data.columnSize === 3 && (
              <i
                className={`e-widget-icon e-widget-icon--md cmd-icon cmd-icon-rotation-speed ${isWidgetDisabled(
                  widgetData
                )}`}
              />
            )}
          </div>
          <div className="col">
            {props.data.hasTitle && props.data.columnSize === 3 && (
              <h4 className="e-widget-title">{longName}</h4>
            )}
            <div className={`e-widget-value e-widget-value--md ${isWidgetDisabled(widgetData)}`}>
              {props.data.columnSize === 2 &&
                getWidgetValue(widgetData, props.data.hasUnitType, valueTypeShort)}
              {props.data.columnSize > 2 &&
                getWidgetValue(widgetData, props.data.hasUnitType, valueTypeLong)}
            </div>
          </div>
        </div>
      )}
      {!props.data.hasIcon && props.data.columnSize > 1 && (
        <div
          data-cy="rpm-widget-value-size-more-1"
          className={`e-widget-value e-widget-value--md ${isWidgetDisabled(widgetData)}`}
        >
          {getWidgetValue(widgetData, props.data.hasUnitType, valueTypeLong)}
        </div>
      )}
      {props.data.hasIcon && props.data.columnSize === 1 && (
        <i
          data-cy="rpm-widget-icon-size-1"
          className={`e-widget-icon cmd-icon cmd-icon-rotation-speed ${isWidgetDisabled(
            widgetData
          )}`}
        />
      )}
      {props.data.columnSize === 1 && (
        <div
          data-cy="rpm-widget-value-size-1"
          className={`e-widget-value ${isWidgetDisabled(widgetData)}`}
          style={{ textAlign: 'center' }}
        >
          {getWidgetValue(widgetData, props.data.hasUnitType, valueTypeShort)}
        </div>
      )}
    </div>
  );
}
